/**
 * Compliance — the units waiting on somebody to read a title deed.
 *
 * THE ERRAND THIS EXISTS FOR. A landlord who signs up today hands in their
 * identity documents, a desk verifies them, they add their first unit — and
 * then cannot invite a tenant to it:
 *
 *   "this unit is not yet proven to be yours, so nothing can start on it"
 *
 * That refusal is the founder's rule and it is correct: "Every unit must be
 * proven owned before it can operate. Proof is recorded by a compliance seat,
 * never claimed by the landlord."
 *
 * WHAT WAS MISSING WAS THE SEAT. The landlord's half was fully built — they can
 * name the stand, upload a deed, and read their own submission back. On the
 * other side there was no queue listing what had been submitted, nothing
 * anywhere that could refuse a document, and no caller for the recording
 * function in either application. A landlord put a title deed into a queue
 * nobody could see and could never be told no.
 *
 * TWO DECISIONS, NOT ONE, and that is the point of the screen. Before script
 * 276 a desk could only ever say yes: property_verifications.rejection_reason
 * was a column the landlord's own screen displayed and no code path could
 * populate. A forged deed had no outcome at all.
 *
 * NO NAMES. The queue carries the stand — erf, township, address — because
 * matching those against the document IS the job. It does not carry the
 * landlord's name, and deliberately not properties.owner_name, which on a
 * managing-agent mandate is a natural person's. Prohibition 6.
 *
 * IT DECIDES NOTHING FOR THE DESK. No match, no tick, no colour on whether the
 * document looks right. Immutable Rule 4 at the last place it could be broken:
 * a verdict rendered on a compliance screen carries that desk's authority
 * without its judgement.
 */

import { useEffect, useState } from 'react';
import {
  O, SANS, Card, StatusPill, Notice, PrimaryCTA, QuietButton, Spinner,
  Field, textInputStyle,
} from '../ui';
import {
  fetchOwnershipQueue, recordOwnershipProof, refuseOwnershipProof,
  openOwnershipDocument,
} from '../lib/compliance';

/**
 * THE DOCUMENT ITSELF.
 *
 * The founder, testing the day before launch: "I saw the document in the queue,
 * but I couldn't open it to actually see what kind of document it was."
 *
 * This screen has always printed `document_path` -- the raw storage key -- as
 * text, and its only button said "Open this one", which opens the decision
 * form. Meanwhile the standing text above the queue tells the desk to "open the
 * document, match the erf number and township against what is on it". A screen
 * that instructs somebody to do a thing it gives them no way to do is worse
 * than one that says nothing, because they conclude they are holding it wrong.
 *
 * A NEW TAB, NOT AN EMBED. A signed URL points at a PDF or a photograph of a
 * title deed; the browser already knows how to render both, and an iframe in a
 * compliance tool is a second viewer to get wrong. Same shape as the provider
 * document viewer, which has worked since script 73.
 *
 * AND THE REFUSAL IS SHOWN. A signed URL can fail -- a moved object, or a
 * storage policy that does not admit this desk -- and a button that silently
 * does nothing is the defect this replaces, in a different costume.
 */
const OpenTheDocument = ({ path }) => {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const open = async () => {
    setBusy(true); setProblem(null);
    const { data, error } = await openOwnershipDocument(path);
    setBusy(false);
    if (error) { setProblem(error); return; }
    window.open(data, '_blank', 'noopener,noreferrer');
  };

  if (!path) {
    return (
      <p style={{ fontFamily: SANS, fontSize: 12, color: O.inkMuted, margin: '8px 0 0' }}>
        This submission has no document on it.
      </p>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      <QuietButton onClick={open} disabled={busy}>
        {busy ? 'Preparing…' : 'Open the document'}
      </QuietButton>
      {problem && <div style={{ marginTop: 8 }}><Notice tone="critical">{problem}</Notice></div>}
    </div>
  );
};

// The database refuses anything shorter. Saying so before somebody types beats
// letting Postgres throw their sentence away.
const MIN_REASON = 8;
const MIN_REFUSAL = 10;

const PROOF_WORDS = {
  title_deed: 'Title deed',
  municipal_rates_account: 'Municipal rates account',
  managing_agent_mandate: 'Managing agent mandate',
};

const Meta = ({ children }) => (
  <p style={{ margin: '5px 0 0', fontFamily: SANS, fontSize: 12, lineHeight: 1.6, color: O.inkMuted }}>
    {children}
  </p>
);

const waited = (days) => {
  if (typeof days !== 'number') return 'just now';
  if (days === 0) return 'today';
  return `${days} day${days === 1 ? '' : 's'} waiting`;
};

export default function OwnershipQueue() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [chosen, setChosen] = useState(null);
  const [reason, setReason] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState(null);
  const [problem, setProblem] = useState(null);

  async function load() {
    setLoading(true); setError(null);
    const { data, error: err } = await fetchOwnershipQueue();
    setLoading(false);
    if (err) { setError(err); setRows(null); return; }
    setRows(data || []);
  }

  useEffect(() => { load(); }, []);

  const pick = (row) => {
    setChosen(row);
    setReason('');
    setOwnerName('');
    setSaid(null);
    setProblem(null);
  };

  async function decide(accept) {
    setBusy(true); setProblem(null);
    const { data, error: err } = accept
      ? await recordOwnershipProof({
        propertyId: chosen.property_id,
        proofType: chosen.proof_type,
        documentPath: chosen.document_path,
        documentDate: chosen.document_date,
        ownerName,
        reason,
      })
      : await refuseOwnershipProof({ propertyId: chosen.property_id, reason });
    setBusy(false);
    if (err) { setProblem(err); return; }
    setSaid(data?.message || (accept ? 'Recorded.' : 'Refused.'));
    setChosen(null);
    // Read the queue back rather than removing the row on screen. What is drawn
    // here should be what the database holds, not this screen's opinion of it.
    load();
  }

  const tooShort = reason.trim().length < MIN_REASON;
  const tooShortToRefuse = reason.trim().length < MIN_REFUSAL;
  const mandateNeedsName = chosen?.proof_type === 'managing_agent_mandate'
    && !ownerName.trim();

  return (
    <div>
      <p style={{
        fontFamily: SANS, fontSize: 12.5, color: O.inkMuted,
        margin: '0 0 16px', lineHeight: 1.7, maxWidth: 640,
      }}>
        A unit cannot be advertised, invited to, or let until Orchid has read the
        document that says who owns it. These are the ones waiting. Open the
        document, match the erf number and township against what is on it, and
        record what you found — either way.
      </p>

      {said && <div style={{ marginBottom: 14 }}><Notice tone="info">{said}</Notice></div>}
      {error && <div style={{ marginBottom: 14 }}><Notice tone="critical">{error}</Notice></div>}

      {loading && <Spinner />}

      {/* AN EMPTY QUEUE IS A SUCCESS. MDS 2.2 — do not fill it. */}
      {!loading && !error && rows && rows.length === 0 && (
        <Card>
          <p style={{ fontFamily: SANS, fontSize: 13, color: O.ink, margin: 0 }}>
            Nothing is waiting. Every unit that has sent a document has an answer.
          </p>
        </Card>
      )}

      {!loading && rows && rows.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((r) => (
            <Card key={r.property_id}>
              <div style={{
                display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
              }}>
                <p style={{
                  fontFamily: SANS, fontWeight: 700, fontSize: 13.5, color: O.ink, margin: 0,
                }}>
                  Erf {r.erf_number}, {r.township}
                  {r.unit_designator ? ` · Unit ${r.unit_designator}` : ''}
                </p>
                <StatusPill tone={r.days_waiting > 7 ? 'warning' : 'quiet'}>
                  {waited(r.days_waiting)}
                </StatusPill>
                {/* The consequence of a refusal is different for a unit that is
                    already proved — it keeps operating. A desk should know that
                    before it decides, not after. */}
                {r.already_proved && (
                  <StatusPill tone="quiet">Already proved — this is a further document</StatusPill>
                )}
              </div>

              <Meta>
                {r.address}{r.city ? `, ${r.city}` : ''}
              </Meta>
              <Meta>
                {PROOF_WORDS[r.proof_type] || r.proof_type}
                {r.document_date ? ` dated ${r.document_date}` : ' — no date given'}
              </Meta>

              {/* THE STORAGE PATH IS NOT THE DOCUMENT. It used to be printed
                  here, as text, beside the proof type -- which told a desk the
                  file exists and nothing about what is in it. */}
              <OpenTheDocument path={r.document_path} />

              {chosen?.property_id !== r.property_id && (
                <div style={{ marginTop: 12, maxWidth: 260 }}>
                  <QuietButton onClick={() => pick(r)}>Open this one</QuietButton>
                </div>
              )}

              {chosen?.property_id === r.property_id && (
                <div style={{ marginTop: 14, borderTop: `1px solid ${O.hairline}`, paddingTop: 14 }}>
                  {r.proof_type === 'managing_agent_mandate' && (
                    <Field
                      label="Whose stand is it"
                      hint="A mandate is somebody acting for an owner. The owner's name is on the document."
                      htmlFor={`owner-${r.property_id}`}
                    >
                      <input
                        id={`owner-${r.property_id}`}
                        style={textInputStyle()}
                        value={ownerName}
                        onChange={(e) => setOwnerName(e.target.value)}
                      />
                    </Field>
                  )}

                  <Field
                    label="What you found"
                    hint="Recorded before the decision is. On a refusal the landlord reads this sentence, so say what would fix it."
                    htmlFor={`reason-${r.property_id}`}
                  >
                    <input
                      id={`reason-${r.property_id}`}
                      style={textInputStyle()}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Title deed read, erf and township match this unit"
                    />
                  </Field>

                  {problem && (
                    <div style={{ margin: '10px 0' }}>
                      <Notice tone="critical">{problem}</Notice>
                    </div>
                  )}

                  {/* Two buttons that could sit side by side, do — and share a
                      size. The founder's standing rule about button rows. */}
                  <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                    <div style={{ flex: 1 }}>
                      <PrimaryCTA
                        onClick={() => decide(true)}
                        busy={busy}
                        disabled={busy || tooShort || mandateNeedsName}
                      >
                        {busy ? 'Recording…' : 'Ownership is proved'}
                      </PrimaryCTA>
                    </div>
                    <div style={{ flex: 1 }}>
                      <QuietButton
                        onClick={() => decide(false)}
                        disabled={busy || tooShortToRefuse}
                      >
                        Refuse this document
                      </QuietButton>
                    </div>
                  </div>

                  <div style={{ marginTop: 10, maxWidth: 200 }}>
                    <QuietButton onClick={() => setChosen(null)}>Close</QuietButton>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <p style={{ marginTop: 14, fontSize: 11.5, lineHeight: 1.7, color: O.inkFaint, maxWidth: 640 }}>
        record_ownership_proof requires your authenticator and a reason on every
        use. Refusing a document does not retract a proof already on record — a
        unit that is operating keeps operating, because un-proving it would end
        a tenancy by side effect.
      </p>
    </div>
  );
}
